/**
 * Payment Method Distribution Chart Component
 * Shows distribution of payments by method (donut chart)
 */

import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { Box, useColorModeValue } from "@chakra-ui/react";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";

const PaymentMethodChart: React.FC = () => {
  const { formatMoney, revision } = useDisplayCurrency();
  const labelColor = useColorModeValue("#c8cfca", "#c8cfca");
  const textColor = useColorModeValue("#575757", "white");

  // Mock payment method data - percentages of total cash collected
  // Total cash collected = $510,000 (from mockFinancialStats.totalRevenue)
  const totalCashCollected = 510000;
  const paymentMethods = [
    { name: "Credit Card", value: 65, amount: Math.round(totalCashCollected * 0.65) },
    { name: "PayPal", value: 20, amount: Math.round(totalCashCollected * 0.20) },
    { name: "Bank Transfer", value: 10, amount: Math.round(totalCashCollected * 0.10) },
    { name: "Stripe", value: 5, amount: Math.round(totalCashCollected * 0.05) },
  ];

  const chartOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: "donut",
      toolbar: {
        show: false,
      },
    },
    labels: paymentMethods.map((m) => m.name),
    colors: ["#38BBD1", "#3EEAEC", "#06E363", "#10B981"],
    legend: {
      position: "bottom",
      fontSize: "12px",
      fontFamily: "'IBM Plex Sans', sans-serif",
      labels: {
        colors: labelColor,
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      theme: "dark",
      y: {
        formatter: (val: number) => {
          if (val === undefined || val === null) return "0%";
          return `${val.toFixed(1)}%`;
        },
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: "14px",
              fontWeight: 600,
              color: labelColor,
              fontFamily: "'IBM Plex Sans', sans-serif",
            },
            value: {
              show: true,
              fontSize: "20px",
              fontWeight: 700,
              color: textColor,
              fontFamily: "'IBM Plex Sans', sans-serif",
              formatter: function (val: string) {
                return `${val}%`;
              },
            },
            total: {
              show: true,
              label: "Total Collected",
              fontSize: "12px",
              fontWeight: 600,
              color: labelColor,
              fontFamily: "'IBM Plex Sans', sans-serif",
              formatter: function () {
                return formatMoney(totalCashCollected);
              },
            },
          },
        },
      },
    },
  }), [formatMoney, revision, labelColor, textColor, totalCashCollected]);

  const chartSeries = paymentMethods.map((m) => m.value);

  return (
    <Box 
      h="300px" 
      display="flex" 
      alignItems="center" 
      justifyContent="center"
      w="100%"
    >
      <ReactApexChart
        options={chartOptions}
        series={chartSeries}
        type="donut"
        height="100%"
        width="100%"
      />
    </Box>
  );
};

export default PaymentMethodChart;

